import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as Sentry from "@sentry/react";
import { ApiError, NetworkError, ValidationError } from '@/lib/api-errors';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Eye, EyeOff, Check } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { validatePasswordStrength, getPasswordErrorMessage } from "@/utils/password-validation";
import { PasswordValidationResult } from "@/types/password-validation.types";
import { cn } from "@/lib/utils";
import { loadReCaptchaScript, getReCaptchaToken, isReCaptchaReady } from "@/utils/recaptcha";
import { validateEmail, validateName, validatePasswordConfirmation } from "@/services/field-validation.service";
import { PasswordStrengthMeter } from "@/components/auth/PasswordStrengthMeter";
import { authService } from "@/services/auth.service";
import { logger } from "@/utils/logger";

interface SignupFormData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
}

export function RegistrationForm() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setError,
    trigger
  } = useForm<SignupFormData>({
    mode: "onTouched",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: ""
    }
  });

  const password = watch("password");
  const [showPassword, setShowPassword] = useState(false);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidationResult | null>(null);

  // Load reCAPTCHA script on mount
  useEffect(() => {
    const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
    
    if (!siteKey) {
      logger.warn('reCAPTCHA site key not configured');
      return;
    }

    loadReCaptchaScript(siteKey)
      .then(() => {
        setRecaptchaReady(true);
      })
      .catch((error) => {
        logger.error('Failed to load reCAPTCHA', error as Error);
        toast({
          title: "Security check unavailable",
          description: "Registration may be limited. Please refresh the page.",
          variant: "destructive"
        });
      });
  }, []);

  // Validate password with debouncing (300ms) as user types
  useEffect(() => {
    if (!password) {
      setPasswordValidation(null);
      return;
    }

    const timeoutId = setTimeout(() => {
      const validation = validatePasswordStrength(password);
      setPasswordValidation(validation);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [password]);

  const onSubmit = async (data: SignupFormData) => {
    // Validate password confirmation using service
    const confirmValidation = validatePasswordConfirmation(data.password, data.confirmPassword);
    if (!confirmValidation.isValid) {
      setError("confirmPassword", {
        type: "manual",
        message: confirmValidation.error || "Passwords don't match"
      });
      toast({
        title: "Passwords don't match",
        description: "Please make sure both passwords are the same.",
        variant: "destructive"
      });
      return;
    }

    // Validate password strength before submission
    const validation = validatePasswordStrength(data.password);
    if (!validation.isValid) {
      setError("password", {
        type: "manual",
        message: getPasswordErrorMessage(validation)
      });
      toast({
        title: "Password does not meet requirements",
        description: getPasswordErrorMessage(validation),
        variant: "destructive"
      });
      return;
    }

    try {
      // Generate reCAPTCHA token
      let recaptchaToken: string | undefined;
      
      if (recaptchaReady && isReCaptchaReady()) {
        try {
          const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
          recaptchaToken = await getReCaptchaToken('signup', siteKey);
        } catch (error) {
          logger.error('reCAPTCHA token generation failed', error as Error);
          toast({
            title: "Security check failed",
            description: "Please try again. If the problem persists, contact support.",
            variant: "destructive"
          });
          return;
        }
      }

      // Use AuthService for registration
      const result = await authService.initiateRegistration({
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        recaptchaToken
      });

      if (result.success === false) {
        // Handle validation errors
        if (result.error.code === 'VALIDATION_ERROR' && result.error.details?.validationErrors) {
          const validationErrors = result.error.details.validationErrors as Record<string, string[]>;
          
          // Check for reCAPTCHA-specific errors
          if (validationErrors.recaptcha) {
            toast({
              title: "Security verification failed",
              description: validationErrors.recaptcha[0] || "Please try again or contact support.",
              variant: "destructive"
            });
            return;
          }
          
          // Map server errors to form fields
          Object.entries(validationErrors).forEach(([field, errors]) => {
            const fieldName = field as keyof SignupFormData;
            setError(fieldName, {
              type: "server",
              message: errors[0]
            });
          });
          
          toast({
            title: "Validation failed",
            description: result.error.userMessage,
            variant: "destructive"
          });
          return;
        }

        // Handle other errors
        toast({
          title: "Registration failed",
          description: result.error.userMessage,
          variant: "destructive"
        });
        
        Sentry.captureException(new Error(result.error.message), {
          tags: { context: 'registration_error', code: result.error.code },
          extra: { error: result.error }
        });
        return;
      }

      // Success — persist email for verify-email page resilience across reloads
      sessionStorage.setItem('pending_verification_email', data.email);
      
      toast({
        title: "Account created! 🎉",
        description: "Check your inbox for a verification link to continue."
      });
      navigate(`/auth/verify-email?email=${encodeURIComponent(data.email)}`);
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
      Sentry.captureException(error, {
        tags: { context: 'unknown_error', operation: 'registration' },
        extra: { message: 'Unknown registration error' }
      });
    }
  };

  return (
    <Card className="shadow-lg">
      <CardHeader className="text-left pb-6">
        <CardTitle className="text-foreground font-semibold text-2xl">Create your account</CardTitle>
        <p className="text-muted-foreground text-sm">
          Get started with your succession planning journey
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName" className="text-sm font-medium text-foreground">
                First name
              </Label>
              <Input 
                id="firstName" 
                type="text" 
                {...register("firstName", {
                  validate: (value) => {
                    const result = validateName(value, 'First name');
                    return result.isValid || result.error || '';
                  }
                })}
                className={cn("mt-2", errors.firstName && "border-destructive")}
                aria-invalid={errors.firstName ? "true" : "false"}
                aria-describedby={errors.firstName ? "firstName-error" : undefined}
              />
              {errors.firstName && (
                <p id="firstName-error" className="text-sm text-destructive mt-1" role="alert">
                  {errors.firstName.message}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="lastName" className="text-sm font-medium text-foreground">
                Last name
              </Label>
              <Input 
                id="lastName" 
                type="text" 
                {...register("lastName", {
                  validate: (value) => {
                    const result = validateName(value, 'Last name');
                    return result.isValid || result.error || '';
                  }
                })}
                className={cn("mt-2", errors.lastName && "border-destructive")}
                aria-invalid={errors.lastName ? "true" : "false"}
                aria-describedby={errors.lastName ? "lastName-error" : undefined}
              />
              {errors.lastName && (
                <p id="lastName-error" className="text-sm text-destructive mt-1" role="alert">
                  {errors.lastName.message}
                </p>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="email" className="text-sm font-medium text-foreground">
              Email address
            </Label>
            <Input 
              id="email" 
              type="email" 
              {...register("email", {
                validate: (value) => {
                  const result = validateEmail(value);
                  return result.isValid || result.error || '';
                }
              })}
              className={cn("mt-2", errors.email && "border-destructive")}
              aria-invalid={errors.email ? "true" : "false"}
              aria-describedby={errors.email ? "email-error" : undefined}
            />
            {errors.email && (
              <p id="email-error" className="text-sm text-destructive mt-1" role="alert">
                {errors.email.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-medium text-foreground">
              Password
            </Label>
            <div className="relative mt-2">
              <Input 
                id="password" 
                type={showPassword ? "text" : "password"} 
                {...register("password", {
                  required: "Password is required"
                })}
                className={cn(
                  password && password.length > 0 ? "pr-10" : "",
                  errors.password && "border-destructive",
                  passwordValidation?.isValid === false && "border-destructive",
                  passwordValidation?.isValid === true && "border-success"
                )}
                aria-invalid={errors.password || passwordValidation?.isValid === false ? "true" : "false"}
                aria-describedby="password-requirements password-error"
              />
              {password && password.length > 0 && (
                <button 
                  type="button" 
                  className="absolute inset-y-0 right-0 flex items-center pr-3" 
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                </button>
              )}
            </div>

            {/* Password Strength Meter */}
            <PasswordStrengthMeter password={password || ""} className="mt-3" />

            {/* Password requirements checklist */}
            <div id="password-requirements" className="text-sm space-y-1.5 mt-3 p-3 bg-muted/50 rounded-md">
              <p className="font-medium text-foreground mb-2">
                Password must contain:
              </p>
              <PasswordRequirement
                met={passwordValidation?.requirements.minLength ?? false}
                text="At least 12 characters"
              />
              <PasswordRequirement
                met={passwordValidation?.requirements.hasUppercase ?? false}
                text="One uppercase letter (A-Z)"
              />
              <PasswordRequirement
                met={passwordValidation?.requirements.hasLowercase ?? false}
                text="One lowercase letter (a-z)"
              />
              <PasswordRequirement
                met={passwordValidation?.requirements.hasNumber ?? false}
                text="One number (0-9)"
              />
              <PasswordRequirement
                met={passwordValidation?.requirements.hasSpecialChar ?? false}
                text="One special character (!@#$%^&*)"
              />
              <PasswordRequirement
                met={passwordValidation?.requirements.notCommon ?? false}
                text="Not a common password"
              />
            </div>

            {/* Error message */}
            {passwordValidation?.isValid === false && password && password.length > 0 && (
              <div
                id="password-error"
                className="text-sm text-destructive mt-2"
                role="alert"
                aria-live="polite"
              >
                {getPasswordErrorMessage(passwordValidation)}
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="confirmPassword" className="text-sm font-medium text-foreground">
              Confirm password
            </Label>
            <Input 
              id="confirmPassword" 
              type="password" 
              {...register("confirmPassword", {
                required: "Please confirm your password"
              })}
              className={cn("mt-2", errors.confirmPassword && "border-destructive")}
              aria-invalid={errors.confirmPassword ? "true" : "false"}
              aria-describedby={errors.confirmPassword ? "confirmPassword-error" : undefined}
            />
            {errors.confirmPassword && (
              <p id="confirmPassword-error" className="text-sm text-destructive mt-1" role="alert">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          <div className="pt-4">
            <Button 
              type="submit" 
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Creating account..." : "Create account"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

/**
 * Helper component for displaying individual password requirement items
 * Shows a checkbox that turns green when the requirement is met
 */
interface PasswordRequirementProps {
  met: boolean;
  text: string;
}

function PasswordRequirement({ met, text }: PasswordRequirementProps) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={cn(
          "w-4 h-4 rounded border flex items-center justify-center transition-colors duration-150",
          met ? "bg-success border-success" : "border-muted-foreground/30"
        )}
        aria-hidden="true"
      >
        {met && (
          <Check className="w-3 h-3 text-success-foreground" strokeWidth={3} />
        )}
      </div>
      <span className={cn(
        "text-sm transition-colors duration-150",
        met ? "text-success font-medium" : "text-muted-foreground"
      )}>
        {text}
      </span>
    </div>
  );
}